import { Component, OnDestroy, OnInit } from '@angular/core';
import { TableLazyLoadEvent } from 'primeng/table';
import { CompanyEntity } from '../../../domain/entities/company.entity';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { Toast } from 'primeng/toast';
import { CompanyPaginationParamsModel } from '../../../data/models/company-pagination-params.model';
import { Failure } from 'src/app/core/utils/failure';
import { CompanyPagination } from '../../../domain/entities/company-pagination';
import { GetAllPaginationCompaniesUseCase } from '../../../domain/usecases/get-all-pagination-companies.usecase';
import { DeleteCompanyModel } from '../../../data/models/delete-company.model';
import { DeleteCompanyUseCase } from '../../../domain/usecases/delete-company.usecase';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-company-table',
  templateUrl: './company-table.component.html',
  styleUrls: ['./company-table.component.css'],
  providers: [MessageService, Toast, ConfirmationService],
})
export class CompanyTableComponent implements OnInit, OnDestroy {
  selectedCompany?: CompanyEntity;
  selectedCompanyId?: string;

  defaultRows: number = 5;
  defaultPage: number = 1;
  public first: number = 0;
  public itemsPerPage: number = 5;
  public amountRegistersPage = this.itemsPerPage;
  public totalQuantity: number = 0;

  public skeleton: boolean = true;
  public skeletons: any = [{}, {}, {}, {}, {}];

  isLoading: boolean = true;
  homeView: boolean = false;
  paginatedData: CompanyPagination | undefined = undefined;
  allCompanies: CompanyEntity[] = [];

  searchTerm: string = '';
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;
  private lastRequestId: number = 0;

  public items: MenuItem[] = [
    {
      label: 'Editar empresa',
      icon: 'pi pi-pencil',
      command: () => this.editCompany(),
    },
    {
      label: 'Eliminar empresa',
      icon: 'pi pi-trash',
      command: () => this.showOptionPopup(),
    },
  ];

  ngOnInit(): void {
    this.getAllCompanies();
    if (this.router.url.includes('main/home')) {
      this.homeView = true;
    }
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.defaultPage = 1;
        this.first = 0;
        this.getAllCompanies();
      });
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  constructor(
    private router: Router,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private getAllPaginationCompaniesUseCase: GetAllPaginationCompaniesUseCase,
    private deleteCompanyUseCase: DeleteCompanyUseCase
  ) {}

  loadRecords(event: TableLazyLoadEvent) {
    this.defaultPage =
      (event.first || 0) / (event.rows || this.defaultRows) + 1;
    this.itemsPerPage = event.rows || this.defaultRows;
    this.getAllCompanies();
  }

  async getAllCompanies(): Promise<void> {
    this.isLoading = true;
    const requestId = ++this.lastRequestId;
    let params = new CompanyPaginationParamsModel(
      this.searchTerm.trim(),
      this.defaultPage,
      this.itemsPerPage
    );
    let result = await this.getAllPaginationCompaniesUseCase.execute(params);
    // Descarta respuestas de búsquedas anteriores que lleguen tarde
    if (requestId !== this.lastRequestId) {
      return;
    }
    if (result) {
      setTimeout(() => {
        this.skeleton = false;
      }, 500);
    }
    if (result instanceof Failure) {
      this.allCompanies = [];
      this.totalQuantity = 0;
      this.skeleton = false;
      this.isLoading = false;
      return;
    }
    this.allCompanies = result.response || [];
    this.totalQuantity = result.totalQuantity;
    this.isLoading = false;
  }

  onFilter() {
    this.searchSubject.next(this.searchTerm.trim());
  }

  editCompany(): void {
    if (this.selectedCompanyId) {
      this.router.navigate(['main/company/update', this.selectedCompanyId]);
    }
  }

  async deleteCompany(): Promise<void> {
    this.isLoading = true;
    let company = new DeleteCompanyModel(this.selectedCompanyId!);
    let result = await this.deleteCompanyUseCase.execute(company);
    this.isLoading = false;
    if (result instanceof Failure) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: result.message,
      });
      return;
    }
    this.messageService.add({
      severity: 'success',
      summary: 'Empresa eliminada',
      detail: 'La empresa se eliminó exitosamente.',
    });
    this.getAllCompanies();
  }

  showOptionPopup() {
    this.confirmationService.confirm({
      header: 'Eliminar empresa',
      message: `¿Está seguro de que desea eliminar la empresa? Se perderán todos los datos.`,
      accept: () => {
        this.deleteCompany();
      },
    });
  }

  selectcompany(id: string) {
    this.selectedCompanyId = id;
  }

  changePage(event: any) {
    this.first = event.first;
    this.defaultPage = event.page + 1;
    this.itemsPerPage = event.rows;
    this.skeleton = true;
    this.getAllCompanies();
  }
}
